import { CssVarsThemeOptions } from "@mui/material/styles";
import { paletteShade } from "@themes/utils/paletteShade";
import { sharedNeutrals } from "@themes/tokens/shared";

import { buttonIconSx } from "./buttons";
import { getInputDisabledStyles } from "./inputs";

type ThemeComponents = NonNullable<CssVarsThemeOptions["components"]>;

export const SELECT_DROPDOWN_PAPER_CLASS_NAME = "SocialProSelectDropdown-paper";
export const SELECT_DROPDOWN_LIST_CLASS_NAME = "SocialProSelectDropdown-list";

/** Shared hover / selected / focus-visible for menu-like controls (light + dark via palette vars). */
const menuLikeInteractionStyles = (theme: any) => ({
  "&.Mui-focusVisible:not(.Mui-disabled)": {
    backgroundColor: theme.vars.palette.action.focus
  },
  "&.Mui-selected": {
    backgroundColor: theme.vars.palette.action.selected,
    color: theme.vars.palette.text.inverse
  },
  "&.Mui-selected:hover:not(.Mui-disabled)": {
    backgroundColor: theme.vars.palette.action.selected,
    color: theme.vars.palette.text.inverse
  },
  "&:not(.Mui-disabled):focus-visible": {
    backgroundColor: theme.vars.palette.action.focus
  },
  "&:not(.Mui-disabled):hover": {
    backgroundColor: theme.vars.palette.action.hover,
    color: sharedNeutrals.black,
    ...theme.applyStyles("dark", {
      color: theme.vars.palette.text.primary
    })
  },
  fontSize: theme.typography.body2.fontSize,
  marginBottom: theme.spacing(0.5)
});

const selectDropdownItemStyles = (theme: any) => ({
  "& .MuiTypography-root": {
    fontSize: "inherit"
  },
  fontSize: theme.typography.body2.fontSize,
  ...theme.applyStyles("dark", {
    "&.Mui-focusVisible:not(.Mui-disabled)": {
      backgroundColor: `rgb(${theme.vars.palette.text.primaryChannel} / 0.12)`
    },
    "&.Mui-selected": {
      backgroundColor: `rgb(${theme.vars.palette.primary.lightChannel} / 0.22)`,
      color: theme.vars.palette.text.primary
    },
    "&.Mui-selected:hover:not(.Mui-disabled)": {
      backgroundColor: `rgb(${theme.vars.palette.primary.lightChannel} / 0.28)`,
      color: theme.vars.palette.text.primary
    },
    "&:not(.Mui-disabled):focus-visible": {
      backgroundColor: `rgb(${theme.vars.palette.text.primaryChannel} / 0.12)`
    },
    "&:not(.Mui-disabled):hover": {
      backgroundColor: `rgb(${theme.vars.palette.text.primaryChannel} / 0.08)`,
      color: theme.vars.palette.text.primary
    },
    color: theme.vars.palette.text.primary
  })
});

export const createMenuOverrides = (): ThemeComponents => ({
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...menuLikeInteractionStyles(theme)
      })
    }
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        padding: 8
      },
      paper: ({ theme }) => ({
        [`&.${SELECT_DROPDOWN_PAPER_CLASS_NAME}`]: theme.applyStyles("dark", {
          backgroundColor: theme.vars.palette.surface.overlay,
          border: `1px solid ${theme.vars.palette.border.subtle}`,
          boxShadow: theme.vars.appEffects.shadow.lg,
          color: theme.vars.palette.text.primary
        })
      })
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...menuLikeInteractionStyles(theme),
        // A picked row is a light green a shade deeper than the palette's
        // palest step, with dark text — not solid primary — so the grey
        // sub-line under a label stays readable. Dark mode keeps its own.
        "&.Mui-selected": {
          backgroundColor: `color-mix(in srgb, ${theme.vars.palette.primary.main} 6%, ${paletteShade(theme, "primary", 50)})`,
          color: theme.vars.palette.primary.main
        },
        "&.Mui-selected:hover:not(.Mui-disabled)": {
          backgroundColor: `color-mix(in srgb, ${theme.vars.palette.primary.main} 10%, ${paletteShade(theme, "primary", 50)})`,
          color: theme.vars.palette.primary.main
        },
        ...theme.applyStyles("dark", {
          "&.Mui-selected, &.Mui-selected:hover:not(.Mui-disabled)": {
            backgroundColor: theme.vars.palette.action.selected,
            color: theme.vars.palette.text.inverse
          }
        }),
        // Menu icons match the button that opens the menu.
        ...buttonIconSx(theme),
        [`.${SELECT_DROPDOWN_LIST_CLASS_NAME} &`]:
          selectDropdownItemStyles(theme),
        borderRadius: theme.shape.borderRadius
      })
    }
  },
  MuiSelect: {
    defaultProps: {
      MenuProps: {
        slotProps: {
          list: {
            className: SELECT_DROPDOWN_LIST_CLASS_NAME
          },
          paper: {
            className: SELECT_DROPDOWN_PAPER_CLASS_NAME
          }
        }
      }
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-disabled": getInputDisabledStyles(theme),
        backgroundColor: theme.vars.palette.background.default,
        fontSize: theme.typography.body2.fontSize
      })
    }
  }
});
